import React, { useCallback, useContext, useMemo } from "react";
import { map } from "lodash";
import {
  COMMENT_OBJECT_TYPE,
  FEATURE_FLAG_NAME,
  SUBMISSION_TAB,
} from "common/constants";
import { SubmissionDefaultValuesContextV2 } from "common/services/formBuilderService/defaults";
import {
  GetSisdRuleDataQuery,
  GetSubmissionQuery,
} from "../../generated/graphql";
import useNavigationTab from "../../hooks/useNavigationTabs";
import { AuthContext } from "../Authorization/AuthContext";
import { TabGroupContainer } from "../Common/__styles__/TabGroup";
import TabGroup from "../Common/TabGroup";
import { Thread } from "../Threads/Thread";
import { Stack } from "./__styles__/Submission";
import { FormComponent, FormDataType } from "./Form";

export const Content = ({
  submission,
  regeneratingPaths,
  handleRegenerateAIFields,
  setFormDisabled,
  propertyProp,
  defaultValuesContext,
  serverProvidedFormData,
  onSubmit,
  account,
  submitFormRef,
}: {
  submission: NonNullable<GetSubmissionQuery["submission"]>;
  regeneratingPaths: Record<string, boolean>;
  handleRegenerateAIFields: (
    formData: FormDataType,
    paths: string[],
  ) => Promise<void>;
  setFormDisabled: (disabled: boolean) => void;
  propertyProp: {
    FIRMInfo?: GetSisdRuleDataQuery["FIRMInfo"];
  };
  defaultValuesContext: SubmissionDefaultValuesContextV2;
  serverProvidedFormData: FormDataType | null;
  onSubmit: (formData: FormDataType) => Promise<void>;
  account: GetSisdRuleDataQuery["account"];
  submitFormRef: React.RefObject<HTMLButtonElement>;
}) => {
  const { isFeatureEnabled } = useContext(AuthContext);
  const submissionCommentsEnabled = isFeatureEnabled(
    FEATURE_FLAG_NAME.SUBMISSION_COMMENTS,
  );

  const tabs = useMemo(
    () => [
      { label: "Form", value: SUBMISSION_TAB.FORM },
      ...(submissionCommentsEnabled && submission.canViewComments ?
        [{ label: "Comments", value: SUBMISSION_TAB.COMMENTS }]
      : []),
    ],
    [submissionCommentsEnabled, submission.canViewComments],
  );

  const allowedTabs = useMemo(() => map(tabs, "value"), [tabs]);
  const defaultTab = useMemo(() => tabs[0]!.value, [tabs]);

  const [tab, setTab] = useNavigationTab({
    defaultTab,
    allowedTabs,
  });

  const handleOnChange = useCallback(
    ({
      isDirty,
      documentUploadsInProgress,
    }: {
      isDirty: boolean;
      documentUploadsInProgress: boolean;
    }) => {
      setFormDisabled(!isDirty || documentUploadsInProgress);
    },
    [setFormDisabled],
  );

  const canUpdateSubmission = submission.canUpdate ?? false;

  return (
    <Stack>
      <TabGroupContainer>
        <TabGroup currentTab={tab} setTab={setTab} tabs={tabs} />
      </TabGroupContainer>
      {tab === "form" && (
        <FormComponent
          readonly={!canUpdateSubmission}
          submissionType={{
            currentVersion: submission.submissionTypeVersion,
            modules: submission.submissionTypeVersion.submissionType.modules,
            id: submission.submissionTypeVersion.submissionType.id,
          }}
          existingSubmission={submission}
          relatedSubmissions={submission.relatedSubmissions}
          onSubmit={onSubmit}
          onChange={handleOnChange}
          submitFormRef={submitFormRef}
          dynamicDefaultValues={defaultValuesContext}
          account={account}
          property={propertyProp}
          loadingFormPaths={regeneratingPaths}
          regenerateAIFields={handleRegenerateAIFields}
          serverProvidedFormData={serverProvidedFormData ?? undefined}
        />
      )}
      {tab === "comments" && submission.canViewComments && (
        <Thread
          objectId={submission.id}
          objectType={COMMENT_OBJECT_TYPE.SUBMISSION}
          canViewComments={submission.canViewComments}
          canCreateComments={submission.canCreateComments}
        />
      )}
    </Stack>
  );
};
